import { TextField, useForm, useModal } from "@refinedev/antd";
import { errorNotification, successNotification } from "../../../../helpers";
import { FC, useEffect } from "react";
import { Button, Form, Input, InputNumber, Modal, Radio, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useWatch } from "antd/es/form/Form";
import { streamValidator } from "../../../../validators";
import { Match } from "../../Lib/types";

type selectType = { key: string; value: string };

const VK_ID_TYPE_LINK = "vk-id";
const VK_ONLINE_TYPE_LINK_VIDEO = "video";

const BROADCAST_INPUT_TYPE = {
  LINK_URL: "LINK_URL",
  VK_ID: "VK_ID",
} as const;

type BroadcastInputType =
  (typeof BROADCAST_INPUT_TYPE)[keyof typeof BROADCAST_INPUT_TYPE];

type TranslationFormValues = {
  type?: string;
  typeLink?: string;
  broadcastInputType?: BroadcastInputType;
  url?: string;
  vidOid?: number | null;
  vidId?: number | null;
};

type MatchesTranslationAddProps = {
  matchId: number;
  leagueId: number;
  fnlId?: Match["fnlId"];
  onRefetchCard: () => void;
  intersectionSelectOption: selectType[];
  disableBroadcast?: boolean;
};
export const MatchesTranslationAdd: FC<MatchesTranslationAddProps> = ({
  matchId,
  leagueId,
  fnlId,
  onRefetchCard,
  intersectionSelectOption,
  disableBroadcast = false,
}) => {
  const { modalProps, show, close } = useModal();

  const { formProps, form } = useForm<TranslationFormValues>({
    action: "create",
    resource: "translations",
    meta: {
      matchId,
      leagueId,
      fnlId,
    },
    dataProviderName: "translationsDataProvider",
    redirect: false,
    ...successNotification({
      successData: {
        message: "Медиа блок добавлен",
        type: "success",
      },
      callback: () => {
        close();
        form.resetFields();
        onRefetchCard();
      },
    }),
    ...errorNotification({}),
  });

  const typeLink = useWatch("typeLink", form);
  const watchType = useWatch("type", form);
  const watchBroadcastInputType = useWatch("broadcastInputType", form);

  const isVkId = watchType === "LINK" && typeLink === VK_ID_TYPE_LINK;
  const isBroadcastVkId =
    watchType === "BROADCAST" &&
    watchBroadcastInputType === BROADCAST_INPUT_TYPE.VK_ID;

  const typeLinkOptions = intersectionSelectOption.flatMap((translation) =>
    translation.key === "vk"
      ? [translation, { key: VK_ID_TYPE_LINK, value: "VK ID" }]
      : [translation]
  );

  useEffect(() => {
    if (watchType !== "BROADCAST") {
      return;
    }

    const currentTypeLink = form.getFieldValue("typeLink");
    if (currentTypeLink !== VK_ONLINE_TYPE_LINK_VIDEO) {
      form.setFieldValue("typeLink", VK_ONLINE_TYPE_LINK_VIDEO);
    }
  }, [watchType, form]);

  useEffect(() => {
    if (watchType !== "BROADCAST") {
      form.setFieldValue("broadcastInputType", undefined);
      return;
    }

    const currentInputType = form.getFieldValue("broadcastInputType");
    if (
      currentInputType !== BROADCAST_INPUT_TYPE.LINK_URL &&
      currentInputType !== BROADCAST_INPUT_TYPE.VK_ID
    ) {
      form.setFieldValue("broadcastInputType", BROADCAST_INPUT_TYPE.LINK_URL);
    }
  }, [watchType, form]);

  const availableTypeLinkOptions = typeLinkOptions;

  const handleChangeTypeLink = () => {
    form.setFieldValue("url", null);
    form.setFieldValue("vidOid", null);
    form.setFieldValue("vidId", null);
  };

  const handleChangeType = () => {
    form.setFieldValue("typeLink", undefined);
    form.setFieldValue("broadcastInputType", undefined);
    handleChangeTypeLink();
  };

  const handleChangeBroadcastInputType = () => {
    handleChangeTypeLink();
  };

  const handleFinish = (rawValues: unknown) => {
    const values = rawValues as TranslationFormValues;
    const { vidOid, vidId, url, broadcastInputType, ...rest } = values;

    if (values.type === "LINK") {
      if (values.typeLink !== VK_ID_TYPE_LINK) {
        return formProps.onFinish?.({ ...rest, url });
      }

      return formProps.onFinish?.({
        ...rest,
        typeLink: "vk",
        vidOid,
        vidId,
      });
    }

    if (values.type === "BROADCAST") {
      if (broadcastInputType === BROADCAST_INPUT_TYPE.VK_ID) {
        return formProps.onFinish?.({
          ...rest,
          typeLink: VK_ONLINE_TYPE_LINK_VIDEO,
          url: undefined,
          vidOid,
          vidId,
        });
      }

      return formProps.onFinish?.({
        ...rest,
        typeLink: VK_ONLINE_TYPE_LINK_VIDEO,
        url,
      });
    }

    return formProps.onFinish?.({ ...rest, url });
  };

  const renderText = () => {
    switch (typeLink) {
      case VK_ID_TYPE_LINK:
        return "VK ID. Укажите OID и ID из embed-ссылки VK.";
      case "vk":
        return `ВК. \n
        Для добавления ссылки необходимо авторизироваться в ВК. Ссылка добавляется на ролик через кнопку Share – Export. Общий формат ссылки в вк <iframe src="https://vk.com/video_ext.php?oid=-29484355&id=456247241&hd=2" width="853" height="480" allow="autoplay; encrypted-media; fullscreen; picture-in-picture;" frameborder="0" allowfullscreen></iframe>. Добавляется только выделенная часть https://vk.com/video_ext.php?oid=-29484355&id=456247241&hd=2.`;
      case "match-tv":
        return `Матч ТВ.
        Должна начинаться с video.matchtv.ru`;
      case "match-premier":
        return `Матч премьер. Должна начинаться
        с matchpremier.ru`;
      case "kinopoisk":
        return `Кинопоиск. Должна начинаться с (https://)
        kinopoisk.ru или (https://) hd.kinopoisk.ru`;
      case "tricolor":
        return "Триколор. Должна начинаться с tricolor.ru или tricolor.tv";
      default:
        return "";
    }
  };

  return (
    <>
      <Button onClick={show} icon={<PlusOutlined />}>
        Добавить трансляцию
      </Button>
      <Modal
        title={"Добавление трансляции"}
        onCancel={close}
        {...modalProps}
        footer={<></>}
      >
        <Form
          style={{
            width: "100%",
          }}
          {...formProps}
          onFinish={handleFinish}
          initialValues={{ ...formProps.initialValues, type: "LINK" }}
          layout={"vertical"}
        >
          <Form.Item name={"type"} label={"Тип трансляции:"}>
            <Radio.Group onChange={handleChangeType}>
              <Radio value={"LINK"}>Ссылка на трансляцию</Radio>
              <Radio value={"BROADCAST"} disabled={disableBroadcast}>
                Онлайн трансляция на сайте
              </Radio>
            </Radio.Group>
          </Form.Item>
          {watchType === "BROADCAST" ? (
            <>
              <Form.Item hidden name={"typeLink"}>
                <Input />
              </Form.Item>

              <Form.Item
                name={"broadcastInputType"}
                label={"Тип медиа"}
                rules={[
                  {
                    required: true,
                    message: "Необходимо выбрать тип медиа",
                  },
                ]}
              >
                <Select
                  placeholder="Выберите тип медиа"
                  notFoundContent={"Тип не найден"}
                  onChange={handleChangeBroadcastInputType}
                >
                  <Select.Option value={BROADCAST_INPUT_TYPE.LINK_URL}>
                    <TextField value={"Ссылка"} />
                  </Select.Option>
                  <Select.Option value={BROADCAST_INPUT_TYPE.VK_ID}>
                    <TextField value={"VK ID"} />
                  </Select.Option>
                </Select>
              </Form.Item>

              {isBroadcastVkId ? (
                <>
                  <Form.Item
                    name={"vidOid"}
                    label={"OID"}
                    rules={[
                      {
                        required: true,
                        message: "Необходимо указать OID",
                      },
                    ]}
                  >
                    <InputNumber
                      precision={0}
                      controls={false}
                      placeholder={"Например 29484355"}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item
                    name={"vidId"}
                    label={"ID"}
                    rules={[
                      {
                        required: true,
                        message: "Необходимо указать ID",
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      precision={0}
                      controls={false}
                      placeholder={"Например 456247241"}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </>
              ) : (
                <Form.Item
                  name={"url"}
                  label={"Ссылка на трансляцию"}
                  rules={[
                    {
                      required: true,
                      message: "Необходимо указать ссылку",
                    },
                    streamValidator("vk"),
                  ]}
                >
                  <Input placeholder={"Укажите ссылку на трансляцию"}></Input>
                </Form.Item>
              )}
            </>
          ) : (
            <>
              <Form.Item
                name={"typeLink"}
                label={"Тип трансляции:"}
                rules={[
                  {
                    required: true,
                    message: "Необходимо выбрать тип трансляции",
                  },
                ]}
              >
                <Select
                  allowClear
                  showSearch
                  placeholder="Выберите тип трансляции"
                  notFoundContent={"Трансляция не найдена"}
                  onChange={handleChangeTypeLink}
                >
                  {availableTypeLinkOptions.map((translation) => {
                    return (
                      <Select.Option
                        key={translation.key}
                        value={translation.key}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            height: "100%",
                          }}
                        >
                          <TextField value={translation.value} />
                        </div>
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
              {isVkId ? (
                <>
                  <Form.Item
                    name={"vidOid"}
                    label={"OID"}
                    rules={[
                      {
                        required: true,
                        message: "Необходимо указать OID",
                      },
                    ]}
                  >
                    <InputNumber
                      precision={0}
                      controls={false}
                      placeholder={"Например 29484355"}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item
                    name={"vidId"}
                    label={"ID"}
                    rules={[
                      {
                        required: true,
                        message: "Необходимо указать ID",
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      precision={0}
                      controls={false}
                      placeholder={"Например 456247241"}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </>
              ) : (
                <Form.Item
                  name={"url"}
                  label={"Ссылка на трансляцию"}
                  rules={[
                    {
                      required: true,
                      message: "Необходимо указать ссылку",
                    },
                    streamValidator(typeLink),
                  ]}
                >
                  <Input placeholder={"Укажите ссылку на трансляцию"}></Input>
                </Form.Item>
              )}
            </>
          )}

          <Button type={"primary"} htmlType={"submit"}>
            Добавить трансляцию
          </Button>
          <div style={{ padding: "16px 0", color: "grey" }}>{renderText()}</div>
        </Form>
      </Modal>
    </>
  );
};
